import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cursor from 'legacy-src/utility/Cursor';
import TagTreeSelector from 'legacy-src/components/assessment/TagTreeSelector';
import { arrayToTagCategory, getSectionsFilterConfig } from './util';
import FullTextSearch from 'legacy-src/components/assessment/Filters/components/FullTextSearch';
import { SearchGuideModalType } from 'chameleon/legacy/src/components/assessment/Filters/components/SearchGuideModal/types';

const Filters = (props) => {
  const {
    queryParams,
    title,
    statuses,
    onFilterChange,
    onSectionFilterChange,
    sections = [],
    router,
    educationPeriod,
  } = props;
  const tagCategory = arrayToTagCategory(statuses?.options || []);
  const sectionCategory = getSectionsFilterConfig(
    sections
      .filter((section) => Number(educationPeriod) === section.educationPeriod)
      .map((section) => ({ label: section.name, value: section.id })),
  );

  const filtersData = {
    tags: {},
  };
  const filterCursor = useRef(cursor(filtersData, [], onFilterChange, null));

  const sectionFilterCursor = useRef(
    cursor(filtersData, [], onSectionFilterChange, null),
  );

  // Because `cursor` is stored in a `ref` variable which doesn't change
  // over the course of this component life-cycle, the callback (`onFilterChange`)
  // is bound to the first reference seen by this component.
  // And because of this stale condition, it causes a few UI issues,
  // which are only fixed by this usage of `useEffect`.
  // If you have other suggestion, please let me know.
  useEffect(() => {
    filterCursor.current = cursor(filtersData, [], onFilterChange, null);
  }, [queryParams]);

  const isStatusOptionAvailable = useCallback(
    () =>
      queryParams?.status &&
      queryParams.status
        .split(',')
        .every((selectedStatus) =>
          statuses?.options?.find(({ value }) => value === selectedStatus),
        ),
    [queryParams, statuses],
  );

  useEffect(() => {
    sectionFilterCursor.current = cursor(
      filtersData,
      [],
      onSectionFilterChange,
      null,
    );
  }, [queryParams]);

  const isSectionOptionAvailable = useCallback(
    () =>
      queryParams?.section &&
      queryParams.section
        .split(',')
        .every((selectedSection) =>
          sections.find(({ id }) => id === selectedSection),
        ),
    [queryParams, sections],
  );

  const flattenHierarchy = useRef({
    tree: {},
    parentByChild: {},
    filtersByCategory: {
      status: isStatusOptionAvailable() ? queryParams.status.split(',') : null,
    },
  });

  useEffect(() => {
    flattenHierarchy.current = {
      tree: {},
      parentByChild: {},
      filtersByCategory: {
        status: isStatusOptionAvailable()
          ? queryParams.status.split(',')
          : null,
      },
    };
  }, [statuses, queryParams]);

  const sectionsFlattenHierarchy = useRef({
    tree: {},
    parentByChild: {},
    filtersByCategory: {
      section: isSectionOptionAvailable()
        ? queryParams.section.split(',')
        : null,
    },
  });

  useEffect(() => {
    sectionsFlattenHierarchy.current = {
      tree: {},
      parentByChild: {},
      filtersByCategory: {
        section: isSectionOptionAvailable()
          ? queryParams.section.split(',')
          : null,
      },
    };
  }, [sections, queryParams]);

  useEffect(() => {
    //Clear selected filter when 3rd parties changes the status query param
    if (
      filterCursor?.current?.value?.status?.length > 0 &&
      !filterCursor?.current?.value?.status?.includes(queryParams?.status || '')
    ) {
      filterCursor?.current?.requestChange({}, true, true);
    }
  }, [queryParams?.status, filterCursor]);

  useEffect(() => {
    //Clear selected filter when 3rd parties changes the status query param
    if (
      sectionFilterCursor?.current?.value?.section?.length > 0 &&
      !sectionFilterCursor?.current?.value?.section?.includes(
        queryParams?.section || '',
      )
    ) {
      sectionFilterCursor?.current?.requestChange({}, true, true);
    }
  }, [queryParams?.section, sectionFilterCursor]);

  return (
    <div
      className="flex flex-column gap-8 py-4.6 px-8.4 bg-gray-100"
      data-test-id="filters-wrapper"
    >
      <label
        className="text-black text-16 leading-140 font-medium"
        data-test-id="filters-title"
      >
        {title}
      </label>
      <div className="flex gap-x-10 items-start">
        <TagTreeSelector
          key={statuses?.type}
          className="max-w-98 flex-1"
          filterCursor={filterCursor.current}
          tagCategory={tagCategory}
          selectType="multiple"
          flattenHierarchy={flattenHierarchy}
          tagCategoryId="status"
          isFullTextToggleAvailable={false}
          data-test-id="status-filter"
        />
        {statuses?.type === 'assignments' ? (
          <TagTreeSelector
            key="sections"
            className="max-w-98 flex-1"
            selectType="multiple"
            isFullTextToggleAvailable={false}
            tagCategory={sectionCategory}
            tagCategoryId="section"
            flattenHierarchy={sectionsFlattenHierarchy}
            filterCursor={sectionFilterCursor.current}
            data-test-id="section-filter"
          />
        ) : (
          <div />
        )}
        <FullTextSearch
          showTipsButton
          tipsModalPage={SearchGuideModalType.Assessments}
          className="max-w-128 flex-1 ml-auto"
          inputContainerClassName="w-auto"
          searchTerm={queryParams?.searchTerm || ''}
          component={{
            props: {
              location: router.location,
              params: router.params,
              router,
            },
          }}
          data-test-id="full-text-search"
          inputPlaceholder="Search title"
        />
      </div>
    </div>
  );
};

Filters.propTypes = {
  queryParams: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  statuses: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func,
  sections: PropTypes.array.isRequired,
  subject: PropTypes.string.isRequired,
  educationPeriod: PropTypes.string.isRequired,
};

Filters.defaultProps = {
  onFilterChange: () => {},
};

export default Filters;
